import type { CommentReaction, EmojiReactions } from '~/graphql';
import { Button, Text, Tooltip, useThemeColor, useToast } from '@wistia/vhs';
import { styled } from 'styled-components';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { ApolloCache } from '@apollo/client';
import { isNil } from '~/utilities/type-guards';
import {
  useAddCommentReactionMutation,
  useRemoveCommentReactionMutation,
} from './__gql/CommentReactions.gql.gen';
import type { GroupedReaction } from './helpers';
import { generateEmojiString, generateReactionLabel, groupReactionsByEmoji } from './helpers';

const ReactionButton = styled(Button)`
  border-radius: ${({ theme }) => theme.spacing.space01};
  height: ${({ theme }) => theme.spacing.space05};
  padding: ${({ theme }) => `${theme.spacing.space01} 6px`};

  &[data-active='true'],
  &[data-active='true']:hover {
    border: ${({ theme }) => `${theme.color.brandBlue400} 1px solid`};
  }
`;

const LabelContent = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing.space01};
`;

const InteractiveReaction = ({
  emoji,
  reactionGroup,
  commentId,
}: {
  emoji: EmojiReactions;
  reactionGroup: GroupedReaction;
  commentId: string;
}): JSX.Element => {
  const { currentContactReacted, reactions } = reactionGroup;
  const [activeForContact, setActiveForContact] = useState<boolean>(currentContactReacted);
  const showToast = useToast();
  const [brandBlue100, grey200] = useThemeColor(['brandBlue100', 'grey200']);

  const commonMutationOpts = (activeState: boolean, message: string) => ({
    update: (cache: ApolloCache<unknown>) => {
      cache.evict({ id: commentId, fieldName: 'commentReactions' });
    },
    onCompleted: () => setActiveForContact(activeState),
    onError: () =>
      showToast({
        message: `There was an error ${message}.`,
        variant: 'error',
      }),
  });

  const [addReaction] = useAddCommentReactionMutation({
    variables: {
      input: { teamCommentId: commentId, emoji },
    },
    ...commonMutationOpts(true, 'reacting to the comment'),
  });
  const [removeReaction] = useRemoveCommentReactionMutation(
    commonMutationOpts(false, 'removing the reaction'),
  );

  const onClick = () => {
    if (activeForContact) {
      const reactionForContact = reactions.find((reaction) => reaction.belongsToCurrentContact);

      if (isNil(reactionForContact)) {
        return;
      }

      void removeReaction({
        variables: { input: { commentReactionId: reactionForContact.id } },
      });
    } else {
      void addReaction();
    }
  };

  useEffect(() => {
    setActiveForContact(currentContactReacted);
  }, [currentContactReacted]);

  return (
    <Tooltip label={generateReactionLabel(reactions)}>
      <ReactionButton
        onClick={onClick}
        data-active={activeForContact}
        colorOverride={activeForContact ? brandBlue100 : grey200}
      >
        <LabelContent>
          <div>{generateEmojiString(emoji)}</div>
          <Text variant="caption">{reactions.length}</Text>
        </LabelContent>
      </ReactionButton>
    </Tooltip>
  );
};

export const CommentReactions = ({
  commentId,
  reactions,
}: {
  commentId: string;
  reactions: CommentReaction[];
}): JSX.Element => {
  const groupedReactions = Array.from(groupReactionsByEmoji(reactions));

  return (
    <>
      {groupedReactions.map(([emoji, group]) => (
        <InteractiveReaction
          key={emoji}
          emoji={emoji}
          reactionGroup={group}
          commentId={commentId}
        />
      ))}
    </>
  );
};
